/**
 * RBAC, Validation & Entity Provider API Client
 *
 * Split from platform.ts – RBAC management, validation, and entity provider endpoints.
 */

import client from './client';

const api = {
  get: <T = unknown>(path: string, config?: Record<string, unknown>) => client.get<T>(`/api/platform${path}`, config),
  post: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) => client.post<T>(`/api/platform${path}`, data, config),
  put: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) => client.put<T>(`/api/platform${path}`, data, config),
  delete: <T = unknown>(path: string, config?: Record<string, unknown>) => client.delete<T>(`/api/platform${path}`, config),
};

// ============================================================
// VALIDATION INTERFACES & FUNCTIONS
// ============================================================

export interface ValidationIssue {
  code: string;
  message: string;
  path?: string;
  severity: 'error' | 'warning' | 'info';
  suggestion?: string;
}

export interface ValidationResult {
  valid: boolean;
  issues: ValidationIssue[];
  errorCount: number;
  warningCount: number;
}

export async function validateRbacManifest(manifest: Record<string, unknown>): Promise<ValidationResult> {
  const response = await api.post<ValidationResult>('/validate/manifest', manifest);
  return response.data;
}

export async function validateRegistration(
  registryType: string,
  data: Record<string, unknown>,
  pluginId: string,
): Promise<ValidationResult> {
  const response = await api.post<ValidationResult>(`/validate/${registryType}`, { data, pluginId });
  return response.data;
}

// ============================================================
// RBAC INTERFACES
// ============================================================

/**
 * DB-backed role with full permission list.
 */
export interface RbacRole {
  id: string;
  key: string;
  description: string | null;
  isSystem: boolean;
  pluginId: string | null;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * DB-backed permission definition.
 */
export interface RbacPermission {
  id: string;
  key: string;
  description: string;
  scope: 'global' | 'resource';
  parentPermissionKey: string | null;
  category: string | null;
  pluginId: string | null;
  isSystem: boolean;
  createdAt: string;
}

export interface UserPermissionOverride {
  id: string;
  permissionKey: string;
  mode: 'grant' | 'deny';
  resourceId: string | null;
  grantedBy: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface PluginAccessMatrixPlugin {
  id: string;
  name: string;
  version: string;
  requiredPermissions: string[];
  optionalPermissions: string[];
  features: Record<string, string[]>;
  permissions: Array<{ id: string; key: string; description: string | null; category: string | null }>;
}

// ============================================================
// RBAC FUNCTIONS
// ============================================================

export async function getRbacRoles(): Promise<{ roles: RbacRole[] }> {
  const response = await api.get<{ roles: RbacRole[] }>('/rbac/roles');
  return response.data;
}

export async function getRbacRole(roleKey: string): Promise<RbacRole> {
  const response = await api.get<RbacRole>(`/rbac/roles/${encodeURIComponent(roleKey)}`);
  return response.data;
}

export async function createRbacRole(role: {
  key: string;
  description: string;
  permissionKeys: string[];
}): Promise<RbacRole> {
  const response = await api.post<RbacRole>('/rbac/roles', role);
  return response.data;
}

export async function updateRbacRole(
  roleKey: string,
  updates: { description?: string; permissionKeys?: string[] },
): Promise<RbacRole> {
  const response = await api.put<RbacRole>(`/rbac/roles/${encodeURIComponent(roleKey)}`, updates);
  return response.data;
}

export async function getRbacPermissions(): Promise<{ permissions: RbacPermission[] }> {
  const response = await api.get<{ permissions: RbacPermission[] }>('/rbac/permissions');
  return response.data;
}

export async function getRbacPermissionsByCategory(): Promise<{
  categories: Record<string, Array<{ id: string; key: string; description: string; scope: string; isSystem: boolean }>>;
}> {
  const response = await api.get<{
    categories: Record<string, Array<{ id: string; key: string; description: string; scope: string; isSystem: boolean }>>;
  }>('/rbac/permissions/by-category');
  return response.data;
}

export async function getUserRbacRoles(userId: string): Promise<{
  roles: Array<{ id: string; key: string; description: string; isSystem: boolean; permissions: string[] }>;
}> {
  const response = await api.get<{
    roles: Array<{ id: string; key: string; description: string; isSystem: boolean; permissions: string[] }>;
  }>(`/rbac/users/${encodeURIComponent(userId)}/roles`);
  return response.data;
}

export async function getUserRbacPermissions(userId: string): Promise<{ permissions: string[] }> {
  const response = await api.get<{ permissions: string[] }>(`/rbac/users/${encodeURIComponent(userId)}/permissions`);
  return response.data;
}

export async function getUserRbacPermissionOverrides(userId: string): Promise<{ overrides: UserPermissionOverride[] }> {
  const response = await api.get<{ overrides: UserPermissionOverride[] }>(`/rbac/users/${encodeURIComponent(userId)}/permission-overrides`);
  return response.data;
}

export async function getPluginAccessMatrix(): Promise<{ plugins: PluginAccessMatrixPlugin[] }> {
  const response = await api.get<{ plugins: PluginAccessMatrixPlugin[] }>('/rbac/plugin-access-matrix');
  return response.data;
}

export async function assignRbacRole(userId: string, roleKey: string): Promise<{ success: boolean }> {
  const response = await api.post<{ success: boolean }>('/rbac/users/assign-role', { userId, roleKey });
  return response.data;
}

export async function removeRbacRole(userId: string, roleKey: string): Promise<{ success: boolean }> {
  const response = await api.post<{ success: boolean }>('/rbac/users/remove-role', { userId, roleKey });
  return response.data;
}

export async function grantRbacPermission(
  userId: string,
  permissionKey: string,
  expiresAt?: string,
): Promise<{ success: boolean }> {
  const response = await api.post<{ success: boolean }>('/rbac/users/grant-permission', { userId, permissionKey, expiresAt });
  return response.data;
}

export async function denyRbacPermission(
  userId: string,
  permissionKey: string,
): Promise<{ success: boolean }> {
  const response = await api.post<{ success: boolean }>('/rbac/users/deny-permission', { userId, permissionKey });
  return response.data;
}

export async function removeRbacPermissionOverride(
  userId: string,
  permissionKey: string,
): Promise<{ success: boolean }> {
  const response = await api.post<{ success: boolean }>('/rbac/users/remove-permission-override', { userId, permissionKey });
  return response.data;
}

// ============================================================
// ENTITY PROVIDER INTERFACES & FUNCTION
// ============================================================

export interface EntityProviderInfo {
  pluginId: string;
  displayName?: string;
  icon?: string;
}

export interface EntityGraphConfig {
  autoSync?: boolean;
  nodeCategory?: string;
  allowedRelations?: string[];
  nodeLabel?: string;
  indexedAttributes?: string[];
}

export interface EntityRagConfig {
  enabled?: boolean;
  chunkStrategy?: string;
  embeddingModel?: string;
  refreshOnUpdate?: boolean;
  contentField?: string;
  embeddableFields?: string[];
}

export interface EntityProviderEntry {
  entityType: string;
  providers: EntityProviderInfo[];
  /** Display name from unified registry */
  displayName?: string;
  /** Icon from unified registry */
  icon?: string;
  /** UI color from unified registry */
  color?: string;
  /** Plugin that owns this entity type */
  pluginId?: string | null;
  /** Graph integration config */
  graphConfig?: EntityGraphConfig;
  /** RAG integration config */
  ragConfig?: EntityRagConfig;
}

export interface EntityProviderData {
  entityTypes: EntityProviderEntry[];
  stats: {
    totalProviders: number;
    entityTypes: number;
    byPlugin: Record<string, number>;
  };
}

export async function getEntityProviders(): Promise<EntityProviderData> {
  const response = await api.get<EntityProviderData>('/entity-providers');
  return response.data;
}
